import { type Environments, initializePaddle } from "@paddle/paddle-js";
import { useEffect, useId } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { confirmPaidConversionFromCheckout } from "@/lib/analytics";
import { collectMetaMatchFromBrowser } from "@/lib/metaMatch";
import { useTRPCClient } from "@/lib/trpc";
import { useSessionQuery } from "@/queries/queries";
import { appRoute } from "@/routes/appRoute";

export function PaddlePaymentDialog({ open, onClose }: { open: boolean; onClose: (opened: boolean) => void }) {
    const { data: sessionData } = useSessionQuery();
    const targetId = useId();
    const trpcClient = useTRPCClient();
    useEffect(() => {
        if (open) {
            initialize();
        }
    }, [open]);

    async function initialize() {
        const subscriptionData = await trpcClient.subscription.get.query({
            includePaddleSubscription: false,
        });
        const usersInUse = subscriptionData?.usersInUse;
        if (!usersInUse) {
            throw new Error("No users in use found.");
        }
        const paddle = await initializePaddle({
            token: import.meta.env.VITE_PADDLE_CLIENT_TOKEN || "",
            checkout: {
                settings: {
                    variant: "one-page",
                    displayMode: "inline",
                    frameTarget: targetId,
                    frameInitialHeight: 450,
                    frameStyle: "width: 100%; min-width: 312px; background-color: transparent; border: none;",
                },
            },

            eventCallback: (event) => {
                if (event.name === "checkout.completed") {
                    const data = event?.data;
                    trpcClient.subscription.create
                        .mutate({
                            customerId: data!.customer.id!,
                        })
                        .then(() => {
                            // `subscription.create` writes paddleSubscriptionId "temp" until
                            // the webhook persists a real sub_… — do not fire on that.
                            // Fire-and-forget: never await GTM / this poll before redirect.
                            void confirmPaidConversionFromCheckout({
                                checkoutData: data,
                                quantity: usersInUse,
                                organizationId: sessionData?.session?.activeOrganizationId ?? undefined,
                                email: sessionData?.user?.email,
                                plan: import.meta.env.VITE_PADDLE_PRICE_ID,
                                loadSubscription: async () => {
                                    const latest = await trpcClient.subscription.get.query({
                                        includePaddleSubscription: false,
                                    });
                                    return latest?.subscription ?? null;
                                },
                            });
                            setTimeout(() => {
                                window.location.href = "/";
                            }, 2000);
                        });
                }
            },
        });
        if (import.meta.env.VITE_PADDLE_ENV === "sandbox") {
            paddle!.Environment.set(import.meta.env.VITE_PADDLE_ENV as Environments);
        }
        console.log(
            "paddle",
            import.meta.env.VITE_PADDLE_ENV,
            import.meta.env.VITE_PADDLE_PRICE_ID,
            import.meta.env.VITE_PADDLE_CLIENT_TOKEN
        );
        const match = collectMetaMatchFromBrowser();
        paddle!.Checkout.open({
            items: [
                {
                    priceId: import.meta.env.VITE_PADDLE_PRICE_ID || "",
                    quantity: usersInUse,
                },
            ],
            customData: {
                organizationId: sessionData!.session!.activeOrganizationId,
                userId: sessionData!.user!.id,
                // fbp / fbc ride along only after marketing consent. The cookie
                // is unset today, so checkout custom data stays organization + user.
                ...(match
                    ? {
                          marketingConsent: "true",
                          ...(match.fbp ? { fbp: match.fbp } : {}),
                          ...(match.fbc ? { fbc: match.fbc } : {}),
                      }
                    : {}),
            },
            customer: {
                email: sessionData?.user?.email ?? "",
            },
        });
    }

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <form>
                <DialogContent
                    className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto"
                    onInteractOutside={(event) => {
                        event.preventDefault();
                    }}
                    onEscapeKeyDown={(event) => {
                        event.preventDefault();
                    }}
                >
                    <DialogHeader>
                        <DialogTitle>Secure Payment</DialogTitle>
                    </DialogHeader>
                    <div id={targetId} className={targetId}></div>
                </DialogContent>
            </form>
        </Dialog>
    );
}
